import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaEye, FaHeart, FaComment, FaClock, FaCalendar, FaUser, FaSearch, FaRocket } from 'react-icons/fa';
import './PublicBlogPortal.css'; // We'll create this CSS file

const PublicBlogPortal = () => {
  const [blogs, setBlogs] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPublicBlogs();
  }, [category]);

  const fetchPublicBlogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/blogs/public');
      
      let filteredBlogs = response.data.blogs || [];
      
      if (category !== 'all') {
        filteredBlogs = filteredBlogs.filter(b => b.category === category);
      }
      
      setBlogs(filteredBlogs);
    } catch (error) {
      console.error('Failed to fetch public blogs');
    } finally {
      setLoading(false);
    }
  };

  const filteredBlogs = searchTerm
    ? blogs.filter(blog => 
        blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.content.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : blogs;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="cosmic-container">
      {/* Animated Cosmic Background */}
      <div className="stars"></div>
      <div className="stars2"></div>
      <div className="stars3"></div>
      
      {/* Floating Planets */}
      <div className="planet planet-1"></div>
      <div className="planet planet-2"></div>
      <div className="planet planet-3"></div>
      <div className="planet planet-4"></div>
      <div className="planet planet-5"></div>
      
      {/* Shooting Stars */}
      <div className="shooting-star"></div>
      <div className="shooting-star" style={{ animationDelay: '3s' }}></div>
      <div className="shooting-star" style={{ animationDelay: '6s' }}></div>

      {/* Content Overlay */}
      <div className="content-overlay">
        {/* Header */}
        <header className="cosmic-header">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <FaRocket className="text-4xl text-yellow-400 animate-bounce-slow" />
                <div>
                  <h1 className="text-4xl font-bold cosmic-gradient-text">
                    PlatypusBox Universe
                  </h1>
                  <p className="text-purple-200 text-sm mt-1">Exploring the cosmos of knowledge</p>
                </div>
              </div>
              {/*<a*/}
              {/*  href="/login"*/}
              {/*  className="cosmic-button px-6 py-3 rounded-full font-medium"*/}
              {/*>*/}
              {/*  Join the Mission*/}
              {/*</a>*/}
            </div>
          </div>
        </header>

        {/* Search & Filter Bar */}
        <div className="cosmic-search-bar">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-purple-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search the universe..."
                  className="cosmic-input w-full pl-12 pr-4 py-3"
                />
              </div>

              {/* Category Filter */}
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="cosmic-select px-4 py-3"
              >
                <option value="all">All Galaxies</option>
                <option value="technology">Technology Nebula</option>
                <option value="business">Business Sector</option>
                <option value="design">Design Constellation</option>
                <option value="marketing">Marketing Orbit</option>
                <option value="tutorial">Tutorial System</option>
                <option value="news">News Cluster</option>
                <option value="other">Other Dimensions</option>
              </select>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          {loading ? (
            <div className="flex flex-col justify-center items-center h-64">
              <div className="cosmic-loader"></div>
              <p className="text-purple-200 mt-6 text-lg">Loading from the cosmos...</p>
            </div>
          ) : selectedBlog ? (
            /* Single Blog View */
            <div className="max-w-4xl mx-auto">
              <button
                onClick={() => {
                  setSelectedBlog(null);
                  scrollToTop();
                }}
                className="cosmic-back-button mb-6"
              >
                ← Return to Universe
              </button>

              <article className="cosmic-card cosmic-article">
                {/* Featured Image */}
                {selectedBlog.featuredImage && (
                  <div className="cosmic-image-container">
                    <img
                      src={selectedBlog.featuredImage}
                      alt={selectedBlog.title}
                      className="w-full h-96 object-cover rounded-t-2xl"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    <div className="cosmic-image-overlay"></div>
                  </div>
                )}

                <div className="p-8 md:p-12">
                  {/* Category Badge */}
                  <span className="cosmic-badge capitalize">
                    {selectedBlog.category}
                  </span>

                  {/* Title */}
                  <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight text-white">
                    {selectedBlog.title}
                  </h1>

                  {/* Meta Info */}
                  <div className="flex flex-wrap items-center gap-6 text-purple-200 text-sm mb-8 pb-8 border-b border-purple-800">
                    <div className="flex items-center gap-2">
                      <FaUser className="text-yellow-400" />
                      <span>{selectedBlog.authorName}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaCalendar className="text-blue-400" />
                      <span>{new Date(selectedBlog.publishedAt || selectedBlog.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaClock className="text-green-400" />
                      <span>{selectedBlog.readTime || 5} min read</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaEye className="text-pink-400" />
                      <span>{selectedBlog.views || 0} views</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="prose prose-lg max-w-none prose-invert">
                    <div className="text-lg leading-relaxed whitespace-pre-wrap text-gray-100">
                      {selectedBlog.content}
                    </div>
                  </div>

                  {/* Tags */}
                  {selectedBlog.tags && selectedBlog.tags.length > 0 && (
                    <div className="mt-12 pt-8 border-t border-purple-800">
                      <div className="flex flex-wrap gap-2">
                        {selectedBlog.tags.map((tag, index) => (
                          <span key={index} className="cosmic-tag">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Engagement Stats */}
                  <div className="mt-12 pt-8 border-t border-purple-800 flex items-center gap-8 text-purple-200">
                    <div className="flex items-center gap-2">
                      <FaHeart className="text-red-400" />
                      <span>{selectedBlog.likes?.length || 0} Stellar Likes</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaComment className="text-blue-400" />
                      <span>{selectedBlog.comments?.length || 0} Transmissions</span>
                    </div>
                  </div>

                  {/* Call to Action */}
                  <div className="mt-12 cosmic-cta">
                    <p className="text-purple-100 mb-4 text-lg">Ready to join our cosmic journey?</p>
                    <a
                      href="/blogs"
                      className="cosmic-button-large"
                    >
                      <FaRocket className="inline mr-2" />
                      Mail: connect@platypusbox.com
                    </a>
                  </div>
                </div>
              </article>
            </div>
          ) : (
            /* Blog Grid */
            <>
              {/* Hero Section */}
              <div className="text-center mb-12">
                <h2 className="text-5xl md:text-6xl font-bold mb-4 cosmic-gradient-text animate-pulse-slow">
                  Explore the Knowledge Universe
                </h2>
                <p className="text-2xl text-purple-200 max-w-2xl mx-auto">
                  {filteredBlogs.length} celestial articles awaiting discovery
                </p>
              </div>

              {/* Blog Grid */}
              {filteredBlogs.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-2xl text-purple-200">No stars found in this sector</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredBlogs.map((blog) => (
                    <div
                      key={blog._id}
                      onClick={() => {
                        setSelectedBlog(blog);
                        scrollToTop();
                      }}
                      className="cosmic-card cosmic-blog-card"
                    >
                      {/* Featured Image */}
                      {blog.featuredImage && (
                        <div className="cosmic-card-image">
                          <img
                            src={blog.featuredImage}
                            alt={blog.title}
                            className="w-full h-48 object-cover"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                          <div className="cosmic-image-overlay"></div>
                        </div>
                      )}

                      <div className="p-6">
                        {/* Category */}
                        <span className="cosmic-badge-small capitalize mb-3">
                          {blog.category}
                        </span>

                        {/* Title */}
                        <h3 className="text-xl font-bold mb-3 line-clamp-2 text-white hover:text-yellow-300 transition-colors">
                          {blog.title}
                        </h3>

                        {/* Excerpt */}
                        <p className="text-purple-200 text-sm mb-4 line-clamp-3">
                          {blog.excerpt}
                        </p>

                        {/* Meta */}
                        <div className="flex items-center justify-between text-xs text-purple-300 mb-4">
                          <span className="flex items-center gap-1">
                            <FaUser /> {blog.authorName}
                          </span>
                          <span className="flex items-center gap-1">
                            <FaClock /> {blog.readTime || 5} min
                          </span>
                        </div>

                        {/* Stats */}
                        <div className="flex items-center gap-4 text-sm text-purple-300 pt-4 border-t border-purple-800">
                          <span className="flex items-center gap-1">
                            <FaEye className="text-blue-400" /> {blog.views || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <FaHeart className="text-red-400" /> {blog.likes?.length || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <FaComment className="text-green-400" /> {blog.comments?.length || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <footer className="cosmic-footer">
          <div className="max-w-7xl mx-auto px-4 py-12">
            <div className="text-center">
              <h3 className="text-3xl font-bold mb-4 cosmic-gradient-text">
                Join the PlatypusBox Universe
              </h3>
              <p className="text-purple-200 mb-6 text-lg">
                Share knowledge across galaxies
              </p>
              <a
                href="/blogs"
                className="cosmic-button-large"
              >
                <FaRocket className="inline mr-2" />
                Mail: connect@platypusbox.com
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default PublicBlogPortal;
